import { createReducer } from '../lib/createReducer.js';
import mergeByProp from '../../lib/mergeByProp';

const initialState = {
  jwt: {},
  companies: [],
  environments: [],
  apiKeys: [],
  mockDataTypes: [],
  jobTypes: [],
  connections: [],
  users: [],
  userCompaniesByUser: {},
  companyConnections: [],
  companyConnectionConfig: undefined,
  companyLimits: {},
  embeddedEndUserId: null,
  mindCloudEmbeddedEndUserId: null,

  invites: [],
  bundles: [],
  ftpUsers: [],
  features: [],
  featureOptions: [],
  productFeatures: [],
  usage: [],
  subscription: {},
  isBillingLoading: false,
  enterpriseSettings: {},
  enterpriseMessage: null,

  roles: [],
  permissions: [],
  userPermissions: [],
  rolesPermissions: [],
  companyMembers: [],
  activeUserPermissions: [],

  dashboard: {},
  dashboardDate: {},
  incidents: [],
  promptEvalsMetadata: [],
  promptEvals: [],
  userTracking: [],
  integrationsProjects: [],
  projectIssues: [],
  integrationGraphs: {},
  sendgridInstallationId: null,
  sendgridSenderEmails: [],
  linearUsers: [],
  hubspotSupportTeamMemberOptions: [],
  adminUsers: [],
  publicSlackChannelUsers: [],

  platformSecrets: [],
  sharedCompanyContacts: []
};

export default createReducer(initialState, {
  GET_COMPANIES_SUCCESS: (state, payload) => ({
    ...state,
    companies: payload?.data?.result && typeof payload.data.result === 'object' ? payload.data.result : payload.data
  }),

  GET_JOB_TYPES_SUCCESS: (state, payload) => ({
    ...state,
    jobTypes: payload.data
  }),

  GET_CONNECTIONS_SUCCESS: (state, payload) => ({
    ...state,
    connections: payload.data
  }),

  GET_USERS_SUCCESS: (state, payload) => ({
    ...state,
    users: payload.data
  }),

  GET_USER_SUCCESS: (state, payload) => ({
    ...state,
    users: mergeByProp('id', state.users, [payload.data], (a, b) => ({ ...a, ...b }))
  }),

  GET_COMPANY_CONNECTIONS_SUCCESS: (state, payload) => ({
    ...state,
    companyConnections: payload.data
  }),

  PUT_COMPANY_CONNECTION_SUCCESS: (state, payload) => ({
    ...state,
    companyConnections: mergeByProp('id', state.companyConnections, [...payload.data], (a, b) => ({
      ...a,
      ...b
    }))
  }),

  GET_COMPANY_CONNECTION_CONFIG_SUCCESS: (state, payload) => ({
    ...state,
    companyConnectionConfig: payload.data
  }),

  GET_USER_COMPANIES_BY_USER_SUCCESS: (state, payload) => ({
    ...state,
    userCompaniesByUser: {
      ...state.userCompaniesByUser,
      [payload.data.userId]: payload.data.companies || []
    }
  }),

  GET_PRODUCT_FEATURES_SUCCESS: (state, payload) => ({
    ...state,
    features: payload.data.features,
    featureOptions: payload.data.featureOptions,
    productFeatures: payload.data.productFeatures
  }),

  GET_COMPANY_USAGE_SUCCESS: (state, payload) => ({
    ...state,
    usage: payload.data
  }),

  GET_COMPANY_SUBSCRIPTION_SUCCESS: (state, payload) => ({
    ...state,
    subscription: payload.data
  }),

  SET_BILLING_LOADING: (state, payload) => ({
    ...state,
    isBillingLoading: payload
  }),

  SET_ENTERPRISE_SETTINGS: (state, payload) => ({
    ...state,
    enterpriseSettings: payload
  }),

  SET_ENTERPRISE_MESSAGE: (state, payload) => ({
    ...state,
    enterpriseMessage: payload
  }),

  GET_ROLES_SUCCESS: (state, payload) => ({
    ...state,
    roles: payload.data
  }),

  CREATE_ROLE_SUCCESS: (state, payload) => ({
    ...state,
    roles: [...state.roles, payload.data]
  }),

  PUT_ROLE_SUCCESS: (state, payload) => ({
    ...state,
    roles: mergeByProp('id', [...state.roles], [payload.data], (a, b) => ({ ...a, ...b }))
  }),

  DELETE_ROLE_SUCCESS: (state, payload) => ({
    ...state,
    roles: state.roles.filter((role) => role.id !== payload.meta.id)
  }),

  GET_PERMISSIONS_SUCCESS: (state, payload) => ({
    ...state,
    permissions: payload.data
  }),

  POST_PERMISSION_SUCCESS: (state, payload) => ({
    ...state,
    permissions: [...state.permissions, payload.data]
  }),

  PUT_PERMISSION_SUCCESS: (state, payload) => ({
    ...state,
    permissions: mergeByProp('id', [...state.permissions], [payload.data], (a, b) => ({ ...a, ...b }))
  }),

  GET_ACTIVE_USER_PERMISSIONS_SUCCESS: (state, payload) => ({
    ...state,
    activeUserPermissions: payload.data
  }),

  GET_USER_PERMISSIONS_SUCCESS: (state, payload) => ({
    ...state,
    userPermissions: payload.data
  }),

  GET_ALL_ROLES_PERMISSIONS_SUCCESS: (state, payload) => ({
    ...state,
    rolesPermissions: payload.data
  }),

  CREATE_ROLE_PERMISSION_SUCCESS: (state, payload) => ({
    ...state,
    rolesPermissions: mergeByProp(['roleId', 'permissionId'], [...state.rolesPermissions], [payload.data], (a, b) => ({ ...a, ...b }))
  }),

  DELETE_ROLE_PERMISSION_SUCCESS: (state, payload) => {
    const newRolesPermissions = [...state.rolesPermissions];

    for (const el of newRolesPermissions) {
      if (el.roleId === payload?.meta?.roleId && el.permissionId === payload?.meta?.permissionId) {
        newRolesPermissions.splice(newRolesPermissions.indexOf(el), 1);
      }
    }

    return { ...state, rolesPermissions: newRolesPermissions };
  },

  GET_INVITES_SUCCESS: (state, payload) => ({
    ...state,
    invites: payload.data
  }),

  POST_NEW_INVITE_SUCCESS: (state, payload) => ({
    ...state,
    invites: [payload.data, ...state.invites]
  }),

  PUT_INVITE_SUCCESS: (state, payload) => ({
    ...state,
    invites: mergeByProp('id', [...state.invites], [payload.data], (a, b) => ({ ...a, ...b }))
  }),

  DELETE_INVITE_SUCCESS: (state, payload) => ({
    ...state,
    invites: [...state.invites].filter((invite) => invite.id !== payload.meta.inviteId)
  }),

  POST_BUNDLE_SUCCESS: (state, payload) => ({
    ...state,
    bundles: [...state.bundles, payload.data]
  }),

  PUT_BUNDLE_SUCCESS: (state, payload) => ({
    ...state,
    bundles: mergeByProp('id', [...state.bundles], [payload.meta], (a, b) => ({ ...a, ...b }))
  }),

  DELETE_BUNDLE_SUCCESS: (state, payload) => ({
    ...state,
    bundles: [...state.bundles].filter((bundle) => bundle.id !== payload.meta.bundleId)
  }),

  GET_COMPANY_MEMBERS_SUCCESS: (state, payload) => ({
    ...state,
    companyMembers: payload.data
  }),

  GET_WORKFLOW_BLUEPRINT_SHARED_MEMBERS_SUCCESS: (state, payload) => ({
    ...state,
    companyMembers: payload.data
  }),

  GET_WORKFLOW_BLUEPRINT_SHARED_CONTACTS_SUCCESS: (state, payload) => ({
    ...state,
    sharedCompanyContacts: Array.isArray(payload?.data) ? payload.data : []
  }),

  INVITE_MEMBER_SUCCESS: (state, payload) => ({
    ...state,
    companyMembers: [...state.companyMembers, ...payload.data]
  }),

  UPDATE_MEMBER_SUCCESS: (state, payload) => ({
    ...state,
    companyMembers: [...state.companyMembers].map((member) => (member.userId === payload.data.userId ? payload.data : member))
  }),

  DELETE_MEMBER_SUCCESS: (state, payload) => ({
    ...state,
    companyMembers: [...state.companyMembers].filter((member) => member.userId !== payload.meta.userId)
  }),

  DELETE_USER_SUCCESS: (state, payload) => ({
    ...state,
    users: [...state.users].filter((user) => user.id !== payload.meta.userId)
  }),

  CONVERT_COMPANY_SUCCESS: (state, payload) => ({
    ...state,
    companyMembers: [...state.companyMembers, ...payload.data.members],
    companies: [...payload.data.companies]
  }),

  DELETE_COMPANY_SUCCESS: (state, payload) => ({
    ...state,
    companies: [...state.companies].filter((company) => company.id !== payload.data.id)
  }),

  GET_ORG_STATE_SUCCESS: (state, payload) => ({
    ...state,
    companyMembers: payload?.data?.members,
    companyLimits: payload?.data?.limits,
    subscription: payload?.data?.subscription,
    environments: payload?.data?.environments,
    mockDataTypes: payload?.data?.mockDataTypes,
    embeddedEndUserId: payload?.data?.embeddedEndUserId,
    mindCloudEmbeddedEndUserId: payload?.data?.mindCloudEmbeddedEndUserId
  }),

  GET_DASHBOARD_STATE_SUCCESS: (state, payload) => ({
    ...state,
    dashboard: payload.data
  }),

  SET_DASHBOARD_DATE: (state, payload) => ({
    ...state,
    dashboardDate: payload
  }),

  GET_INCIDENTS_SUCCESS: (state, payload) => ({
    ...state,
    incidents: payload.data
  }),

  GET_USER_STATE_SUCCESS: (state, payload) => ({
    ...state,
    bundles: payload?.data?.bundles
  }),

  GET_PROMPT_EVALS_SUCCESS: (state, payload) => ({
    ...state,
    prompts: payload.data
  }),

  GET_PROMPT_EVALS_BY_NAME_SUCCESS: (state, payload) => ({
    ...state,
    promptEvalsMetadata: payload.data
  }),

  GET_PROMPT_EVAL_BY_ID_SUCCESS: (state, payload) => ({
    ...state,
    promptEvals: mergeByProp('id', [...(state.promptEvals || [])], [payload.data], (a, b) => ({ ...a, ...b }))
  }),

  POST_COMPANY_ENVIRONMENT_SUCCESS: (state, payload) => ({
    ...state,
    environments: [...state.environments, payload.data]
  }),

  PUT_COMPANY_ENVIRONMENT_SUCCESS: (state, payload) => ({
    ...state,
    environments: mergeByProp('id', [...state.environments], [payload.data], (a, b) => ({ ...a, ...b }))
  }),

  DELETE_COMPANY_ENVIRONMENT_SUCCESS: (state, payload) => ({
    ...state,
    environments: [...state.environments].filter((environment) => environment.id !== payload.meta.environmentId)
  }),
  GET_USER_TRACKING_SUCCESS: (state, payload) => ({
    ...state,
    userTracking: payload.data
  }),
  PUT_USER_TRACKING: (state, payload) => ({
    ...state,
    userTracking: state.userTracking.map((user) => (user.email === payload.email ? { ...user, [payload.column]: payload.value } : user))
  }),

  POST_USER_TRACKING_COMMENT_SUCCESS: (state, payload) => ({
    ...state,
    userTracking: state.userTracking.map((user) => (user.email === payload.meta.email ? { ...user, comments: [...(user.comments || []), payload.data] } : user))
  }),

  PUT_USER_TRACKING_COMMENT_SUCCESS: (state, payload) => ({
    ...state,
    userTracking: state.userTracking.map((user) =>
      user.email === payload.meta.email
        ? {
            ...user,
            comments: (user.comments || []).map((comment) => (comment.id === payload.meta.commentId ? payload.data : comment))
          }
        : user
    )
  }),

  DELETE_USER_TRACKING_COMMENT_SUCCESS: (state, payload) => ({
    ...state,
    userTracking: state.userTracking.map((user) =>
      user.email === payload.meta.email
        ? {
            ...user,
            comments: (user.comments || []).filter((comment) => comment.id !== payload.meta.commentId)
          }
        : user
    )
  }),

  // Mock Types
  POST_MOCK_TYPE: (state, payload) => ({
    ...state,
    mockDataTypes: mergeByProp('id', [...state.mockDataTypes], [...payload.data])
  }),
  GET_MOCK_TYPES_SUCCESS: (state, payload) => ({
    ...state,
    mockDataTypes: mergeByProp('id', [...state.mockDataTypes], [...payload.data])
  }),
  PUT_MOCK_TYPE: (state, payload) => ({
    ...state,
    mockDataTypes: mergeByProp('id', [...state.mockDataTypes], [...payload.data])
  }),
  DELETE_MOCK_TYPE: (state, payload) => ({
    ...state,
    mockDataTypes: [...state.mockDataTypes].filter((mockType) => mockType.id !== payload.data.id)
  }),
  GET_API_KEYS_SUCCESS: (state, payload) => ({
    ...state,
    apiKeys: payload.data
  }),
  CREATE_API_KEY_SUCCESS: (state, payload) => ({
    ...state,
    apiKeys: [payload.data, ...state.apiKeys]
  }),
  UPDATE_API_KEY_PERMISSIONS_SUCCESS: (state, payload) => ({
    ...state,
    apiKeys: state.apiKeys.map((key) => (key.id === payload.data.id ? { ...key, accessLevel: payload.data.accessLevel, permissions: payload.data.permissions, isAllOrganizations: payload.data.isAllOrganizations === true } : key))
  }),
  REVOKE_API_KEY_SUCCESS: (state, payload) => ({
    ...state,
    apiKeys: state.apiKeys.map((key) => (key.id === payload.meta.keyId ? { ...key, revokedOn: new Date().toISOString() } : key))
  }),
  DELETE_API_KEY_SUCCESS: (state, payload) => ({
    ...state,
    apiKeys: state.apiKeys.filter((key) => key.id !== payload.meta.keyId)
  }),
  PUT_EMBEDDED_END_USER_ID: (state, payload) => ({
    ...state,
    embeddedEndUserId: payload
  }),
  GET_LINEAR_USERS_SUCCESS: (state, payload) => ({
    ...state,
    linearUsers: payload.data
  }),
  GET_HUBSPOT_SUPPORT_TEAM_MEMBER_OPTIONS_SUCCESS: (state, payload) => ({
    ...state,
    hubspotSupportTeamMemberOptions: payload.data
  }),
  GET_ADMIN_USERS_SUCCESS: (state, payload) => ({
    ...state,
    adminUsers: payload.data
  }),
  GET_INTEGRATIONS_PROJECTS_SUCCESS: (state, payload) => ({
    ...state,
    integrationsProjects: payload?.data || []
  }),
  GET_INTEGRATIONS_PROJECT_SUCCESS: (state, payload) => {
    return {
      ...state,
      integrationsProjects: mergeByProp('id', state.integrationsProjects, payload.data, (prev, curr) => {
        return prev.payload && !curr.payload ? prev : curr;
      })
    };
  },
  CLEAR_PROJECT_ISSUES: (state) => ({
    ...state,
    projectIssues: []
  }),
  GET_ALL_ISSUES_SUCCESS: (state, payload) => ({
    ...state,
    projectIssues: payload?.data || []
  }),
  GET_INTEGRATORS_SUCCESS: (state, payload) => ({
    ...state,
    integrators: payload?.data || []
  }),
  GET_INTEGRATORS_PROJECTS_SUCCESS: (state, payload) => ({
    ...state,
    integratorsProjects: payload?.data || []
  }),
  GET_INTEGRATORS_ISSUES_SUCCESS: (state, payload) => ({
    ...state,
    integratorsIssues: payload?.data || []
  }),
  GET_INTEGRATION_GRAPHS_SUCCESS: (state, payload) => ({
    ...state,
    integrationGraphs: payload?.data?.result || {}
  }),

  PUT_PROJECT: (state, payload) => ({
    ...state,
    integrationsProjects: mergeByProp('id', state.integrationsProjects, payload?.data, undefined, { keepPosition: true })
  }),

  PUT_PROJECT_ISSUE: (state, payload) => ({
    ...state,
    projectIssues: mergeByProp('id', state.projectIssues, payload?.data, undefined, { keepPosition: true })
  }),

  PUT_WORKFLOW_BLUEPRINT_SHARED_UPDATE_SUCCESS: (state, payload) => {
    const issue = payload?.data?.issue;
    if (!issue) {
      return state;
    }

    return {
      ...state,
      projectIssues: mergeByProp('id', state.projectIssues, [issue], undefined, { keepPosition: true })
    };
  },

  GET_FTP_USERS_SUCCESS: (state, payload) => ({
    ...state,
    ftpUsers: payload.data
  }),

  GET_FTP_USER_SUCCESS: (state, payload) => ({
    ...state,
    ftpUsers: mergeByProp('id', state.ftpUsers, [payload.data], (a, b) => ({ ...a, ...b }))
  }),

  CREATE_FTP_USER_SUCCESS: (state, payload) => ({
    ...state,
    ftpUsers: [payload.data, ...state.ftpUsers]
  }),

  UPDATE_FTP_USER_SUCCESS: (state, payload) => ({
    ...state,
    ftpUsers: mergeByProp('id', state.ftpUsers, [payload.data], (a, b) => ({ ...a, ...b }))
  }),

  DELETE_FTP_USER_SUCCESS: (state, payload) => ({
    ...state,
    ftpUsers: state.ftpUsers.filter((user) => user.id !== payload.meta.id)
  }),
  SET_SENDGRID_INSTALLATION_ID: (state, payload) => {
    if (payload === state.sendgridInstallationId) {
      return state;
    }
    return {
      ...state,
      sendgridInstallationId: payload,
      sendgridSenderEmails: []
    };
  },
  SET_SENDGRID_SENDER_EMAILS: (state, payload) => ({
    ...state,
    sendgridSenderEmails: payload
  }),
  GET_PUBLIC_SLACK_CHANNEL_USERS_SUCCESS: (state, payload) => ({
    ...state,
    publicSlackChannelUsers: payload
  }),

  UPDATE_SERVICE_USER_SUCCESS: (state, payload) => ({
    ...state,
    linearUsers: mergeByProp('id', state.linearUsers, [payload.data], (a, b) => ({ ...a, ...b }))
  }),

  GET_PLATFORM_SECRETS_SUCCESS: (state, payload) => {
    const appId = payload?.meta?.appId || null;
    if (!appId) {
      return state;
    }

    const incoming = Array.isArray(payload?.data) ? payload.data : [];
    const others = (state.platformSecrets || []).filter((n) => n?.appId !== appId);

    return {
      ...state,
      platformSecrets: [...others, ...incoming]
    };
  },

  GET_WORKFLOW_BLUEPRINT_SHARED_PLATFORM_SECRETS_SUCCESS: (state, payload) => {
    const appId = payload?.meta?.appId || null;
    if (!appId) {
      return state;
    }

    const incoming = Array.isArray(payload?.data) ? payload.data : [];
    const others = (state.platformSecrets || []).filter((n) => n?.appId !== appId);

    return {
      ...state,
      platformSecrets: [...others, ...incoming]
    };
  },

  GET_PLATFORM_SECRET_BY_ID_SUCCESS: (state, payload) => {
    const item = payload?.data || null;
    const id = item?.id || payload?.meta?.id || null;
    if (!id) {
      return state;
    }

    const next = mergeByProp('id', state.platformSecrets || [], [item], (a, b) => ({ ...a, ...b }));
    return { ...state, platformSecrets: next };
  },

  GET_WORKFLOW_BLUEPRINT_SHARED_PLATFORM_SECRET_SUCCESS: (state, payload) => {
    const item = payload?.data || null;
    const id = item?.id || payload?.meta?.id || null;
    if (!id) {
      return state;
    }

    const next = mergeByProp('id', state.platformSecrets || [], [item], (a, b) => ({ ...a, ...b }));
    return { ...state, platformSecrets: next };
  },

  PUT_PLATFORM_SECRET: (state, payload) => {
    const item = payload?.data || payload;
    const id = item?.id;
    const appId = item?.appId;
    if (!id || !appId) {
      return state;
    }

    return {
      ...state,
      platformSecrets: mergeByProp('id', state.platformSecrets || [], [item], (a, b) => ({ ...a, ...b }))
    };
  },

  PUT_WORKFLOW_BLUEPRINT_SHARED_PLATFORM_SECRET_SUCCESS: (state, payload) => {
    const item = payload?.data || null;
    const id = item?.id;
    const appId = item?.appId;
    if (!id || !appId) {
      return state;
    }

    return {
      ...state,
      platformSecrets: mergeByProp('id', state.platformSecrets || [], [item], (a, b) => ({ ...a, ...b }))
    };
  },

  DELETE_PLATFORM_SECRET: (state, payload) => {
    const id = payload?.meta?.id || payload?.id || payload?.platformSecretId || payload?.meta?.platformSecretId;
    if (!id) {
      return state;
    }

    return {
      ...state,
      platformSecrets: (state.platformSecrets || []).filter((n) => n?.id !== id)
    };
  },

  DELETE_WORKFLOW_BLUEPRINT_SHARED_PLATFORM_SECRET_SUCCESS: (state, payload) => {
    const id = payload?.data?.id || payload?.meta?.id || null;
    if (!id) {
      return state;
    }

    return {
      ...state,
      platformSecrets: (state.platformSecrets || []).filter((n) => n?.id !== id)
    };
  }
});
